import React, {
  lazy,
  useEffect,
  useMemo,
  useRef,
  useState,
  MouseEvent,
} from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import Form from "react-bootstrap/Form";
import "./InputBox.scss";
import {
  MaterialReactTable,
  // createRow,
  type MRT_ColumnDef,
  MRT_ColumnFiltersState,
  type MRT_Row,
  type MRT_TableOptions,
  useMaterialReactTable,
  MRT_ToggleFiltersButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFullScreenButton,
} from "material-react-table";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { type User } from "./makeData";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Accordion from "react-bootstrap/Accordion";
import { Container, Row } from "reactstrap";
import axiosInstance from "../../Utils/axiosInstance";
import { backendbaseUrl } from "../../Utils/baseUrl";
import { toast } from "react-toastify";
import styled from "styled-components";
import {
  showDeleteConfirmModal,
  ResetTablePreferencesButton,
} from "../../Components/ConfirmModal";
import { CompactTextField } from "../../Components/SharedComponents/SharedComponents";
import FileUpload from "../../Components/FileUpload/FileUpload";
import BrokerTemplateDownloadButton from "../../Components/TemplateDownloadButton/BrokerTemplateDownloadButton";
import HistoryIcon from "@mui/icons-material/History";
import { useNavigate } from "react-router-dom";
import { useTablePersistence } from "../../hooks/useTablePersistence";
import { TableActionVisibilityMenu } from "../../Components/SharedComponents/TableActionVisibilityMenu";
export const StyledTableFirstSecond = styled.div`
  thead tr:first-child th:first-child {
    width: 4% !important;
  }
  thead tr:first-child th:nth-child(2) {
    width: 1% !important;
  }
`;

const SortableActionButton = ({ id, tooltipText, children }: { id: string; tooltipText: string; children: React.ReactNode }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        {children}
      </div>
    </Tooltip>
  );
};

const Example = () => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});
  const {
    state: tableState,
    resetState: resetTableState,
    setColumnFilters,
    setColumnVisibility,
    setColumnOrder,
    setColumnPinning,
    setColumnSizing,
    setDensity,
    setGlobalFilter,
    setShowColumnFilters,
    setShowGlobalFilter,
    setSorting,
    setPagination,
    setGrouping,
    setActionButtonOrder,
    setActionButtonVisibility,
    setIsFullScreen,
  } = useTablePersistence("strategy_table", {
    density: "comfortable",
    pagination: { pageIndex: 0, pageSize: 15 },
    columnFilters: [],
    columnVisibility: {},
    columnOrder: [],
    columnPinning: {},
    columnSizing: {},
    sorting: [],
    grouping: [],
    globalFilter: "",
    showColumnFilters: true,
    showGlobalFilter: true,
    actionButtonOrder: [],
    actionButtonVisibility: {},
    isFullScreen: false,
  });

  const columnFilters = tableState.columnFilters || [];
  const columnVisibility = tableState.columnVisibility || {};
  const density = tableState.density || "comfortable";
  const globalFilter = tableState.globalFilter;
  const showGlobalFilter = tableState.showGlobalFilter ?? true;
  const showColumnFilters = tableState.showColumnFilters ?? true;
  const sorting = tableState.sorting || [];
  const pagination = tableState.pagination || { pageIndex: 0, pageSize: 15 };
  const columnOrder = tableState.columnOrder || [];
  const columnPinning = tableState.columnPinning || {};
  const actionButtonOrder = tableState.actionButtonOrder || [];
  const actionButtonVisibility = tableState.actionButtonVisibility || {};
  const isFullScreen = tableState.isFullScreen ?? false;

  const defaultButtonOrder = [
    "upload",
    "export_all",
    "history",
    "template",
    "export_selected",
    "delete_selected",
    "reset"
  ];

  const buttonsConfig: Record<string, { label: string, tooltipText: string, description: string }> = {
    "upload": { label: "Upload File", tooltipText: "Upload new template • Drag to reorder", description: "Upload new template" },
    "export_all": { label: "Export All Data", tooltipText: "Export all records • Drag to reorder", description: "Export all records" },
    "history": { label: "History", tooltipText: "View activity history • Drag to reorder", description: "View activity history" },
    "template": { label: "Download Template", tooltipText: "Download empty template • Drag to reorder", description: "Download empty template" },
    "export_selected": { label: "Export Selected Rows", tooltipText: "Export selected • Drag to reorder", description: "Export selected" },
    "delete_selected": { label: "Delete Selected", tooltipText: "Delete selected • Drag to reorder", description: "Delete selected" },
    "reset": { label: "Reset View", tooltipText: "Reset table preferences • Drag to reorder", description: "Reset table preferences" }
  };

  const currentActionButtonOrder = actionButtonOrder.length > 0 ? actionButtonOrder : defaultButtonOrder;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = currentActionButtonOrder.indexOf(active.id as string);
      const newIndex = currentActionButtonOrder.indexOf(over.id as string);
      const newOrder = arrayMove(currentActionButtonOrder, oldIndex, newIndex);
      setActionButtonOrder(newOrder);
    }
  };
  const columnSizing = tableState.columnSizing || {};
  const grouping = tableState.grouping || [];

  const [strategyName, setStrategyName] = useState<string>("");
  const [inputBackgroundColour, setInputBackgroundColour] = useState("");
  const [filterModes, setFilterModes] = useState<
    { mode: string; id: string; value: unknown }[]
  >([]);

  const [showRowResizing, setShowRowResizing] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState(0);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({
    strategyName: "",
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({
    strategyName: false,
  });

  const [openFileUpload, setOpenFileUpload] = useState<boolean>(false);
  const handleFileUploadOpen = () => setOpenFileUpload(true);
  const handleFileUploadClose = () => setOpenFileUpload(false);

  const handleFileUploadSubmit = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file); // The key should match your API expectation

    try {
      const response = await axiosInstance.post(
        `${backendbaseUrl}/paper-app/upload/strategy/`, // Updated endpoint
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Refresh table data after successful upload
      queryClient.invalidateQueries({ queryKey: ["users"] });

      return {
        success: true,
        message: "File uploaded and data added successfully!",
        data: response.data,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to upload file",
      };
    }
  };

  // Validation function (updated)
  const validateField = (field: string, value: string): string | null => {
    if (field === "strategyName" && !validateRequired(value)) {
      return "Strategy Name is Required";
    }
    return null;
  };

  // Handle focus (updated)
  const handleFocus = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // Handle blur validation (updated)
  const validateOnBlur = (value: string): string | null => {
    const error = validateField("strategyName", value);
    setFormErrors((prev) => ({ ...prev, strategyName: error || "" }));
    return error;
  };

  const navigate = useNavigate();
  const handlehistory = () => {
    navigate("/settings/log/history/strategy");
  };

  useEffect(() => {
    //do something when the pagination state changes
  }, [pagination]);

  const columns = useMemo<MRT_ColumnDef<User>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Strategy",
        enableEditing: true,

        muiTableBodyCellProps: ({ row, table }) => ({
          onDoubleClick: () => {
            table.setEditingRow(row);
          },
          sx: {
            height: "10px",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "rgba(44, 4, 4, 0.05)",
            },
            ...(table.getState().editingRow?.id === row.id && {
              backgroundColor: "rgba(0, 128, 0, 0.1)",
              transition: "background-color 0.3s ease",
            }),
          },
        }),

        muiEditTextFieldProps: {
          name: "name", // Ensure the correct field is targeted
          required: true,
          error: !!validationErrors?.name,
          helperText: validationErrors?.name,
          onFocus: () =>
            setValidationErrors((prev) => ({
              ...prev,
              name: undefined,
            })),
        },
      },
    ],
    [validationErrors]
  );

  //call CREATE hook
  const { mutateAsync: createUser, isPending: isCreatingUser } =
    useCreateUser();
  //call READ hook
  const {
    data,
    isError: isLoadingUsersError,
    isFetching: isFetchingUsers,
    isLoading: isLoadingUsers,
  } = useGetData(filterModes, pagination, setTotalCount, sorting);
  //call UPDATE hook
  const { mutateAsync: updateUser, isPending: isUpdatingUser } =
    useUpdateUser();
  //call DELETE hook
  const { mutateAsync: deleteUser, isPending: isDeletingUser } =
    useDeleteUser();

  // CREATE FUNCTION TO ADD NEW A STRATEGY
  const createStrategy = async (strategy: { id: string; name: string }) => {
    if (!strategy.name) {
      toast.error("Strategy name is required!");
      return;
    }
    try {
      // Make an API call to add the strategy
      await axiosInstance.post(
        `${backendbaseUrl}/paper-app/strategies/`,
        strategy
      );
      // Invalidate the query to refetch the updated data
      queryClient.invalidateQueries({ queryKey: ["users"] });
      // Show a success message
      toast.success("Strategy added successfully!");
    } catch (error: any) {
      console.error("Error adding strategy:", error);

      let message = "Failed to add strategy. Please try again.";

      // Case 1: API gave structured field errors
      if (error.response?.data) {
        const apiErrors = error.response.data;

        // If backend sends {"name": ["..."]}
        if (apiErrors.name && Array.isArray(apiErrors.name)) {
          message = apiErrors.name[0];
        }
        // If backend sends { "error": "Something went wrong: {...}" }
        else if (typeof apiErrors.error === "string") {
          const match = apiErrors.error.match(
            /'name': \[ErrorDetail\(string='(.+?)'/
          );
          if (match && match[1]) {
            message = match[1]; // ✅ clean extracted string
          } else {
            message = apiErrors.error; // fallback raw error
          }
        }
      }

      toast.error(message);
    }
  };
  // create by submitting the form
const formCreate = async (e: React.FormEvent<HTMLFormElement>) => {
  e.preventDefault();

  setTouched((prev) => ({ ...prev, strategyName: true })); 

  if (strategyName.trim().length === 0) {
    setFormErrors((prev) => ({ ...prev, strategyName: "Strategy Name is Required" }));
    return;
  }

  if (strategyName.trim().length > 250) {
    setFormErrors((prev) => ({ ...prev, strategyName: "Strategy name cannot exceed 250 characters" }));
    return; 
  }

  const strategy = {
      id: Date.now().toString(), // Generate a unique ID
      name: strategyName.trim(), // Use the strategy name from the input field
  };

  await createStrategy(strategy);

    // Reset the form
  setStrategyName("");
};

  //CREATE action
  const handleCreateUser: MRT_TableOptions<User>["onCreatingRowSave"] = async ({
    values,
    table,
  }) => {
    // Validate the input values
    const newValidationErrors = validateUser(values);
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }

    setValidationErrors({});

    const strategy = {
      id: Date.now().toString(), // Generate a unique ID
      name: values.name.trim(), // Use the name from the table values
    };

    await createStrategy(strategy);

    // Exit creating mode
    table.setCreatingRow(null);
  };

  //UPDATE action
  // const handleeditsave: MRT_TableOptions<User>["onEditingRowSave"] = async ({
  //   values,
  //   table,
  //   row, // Add row parameter to access full row data
  // }) => {
  //   // Log the detailed values and table object
  //   console.log("Saving User - Full Row Original Data:", row.original);
  //   console.log("Saving User - Edited Values:", values);

  //   // Merge the edited values with the original row data
  //   const updatedUser = {
  //     ...row.original, // Spread the original row data first
  //     ...values, // Overlay the edited values on top
  //   };

  //   console.log("Saving User - Merged User Data:", updatedUser);

  //   // Validate user data
  //   const newValidationErrors = validateUser(updatedUser);

  //   // Check for validation errors
  //   if (Object.values(newValidationErrors).some((error) => error)) {
  //     console.log("Validation Errors:", newValidationErrors);
  //     setValidationErrors(newValidationErrors);
  //     return;
  //   }

  //   // Clear previous validation errors
  //   setValidationErrors({});

  //   // Trigger user update mutation
  //   try {
  //     await updateUser(updatedUser); // Pass the full, merged user object

  //     // Exit editing mode
  //     table.setEditingRow(null);

  //   } catch (error) {
  //     console.error("User Update Error:", error);
  //   }
  // };
  const handleeditsave: MRT_TableOptions<User>["onEditingRowSave"] = async ({
    values,
    table,
    row,
  }) => {
    console.log("Saving User - Full Row Original Data:", row.original);
    console.log("Saving User - Edited Values:", values);

    const updatedUser = {
      ...row.original,
      ...values,
    };

    console.log("Saving User - Merged User Data:", updatedUser);

    const newValidationErrors = validateUser(updatedUser);
    if (Object.values(newValidationErrors).some((error) => error)) {
      console.log("Validation Errors:", newValidationErrors);
      setValidationErrors(newValidationErrors);
      toast.error("Please fix validation errors before saving");
      return;
    }

    setValidationErrors({});

    try {
      await updateUser(updatedUser);
      table.setEditingRow(null);
      toast.success("Strategy Updated Successfully");
    } catch (error) {
      console.error("Strategy Update Error:", error);
      toast.error("Failed to Update Strategy");
    }
  };
  //DELETE action
const openDeleteConfirmModal = (row: MRT_Row<User>) => {
  showDeleteConfirmModal({
    title: "Are you sure you want to delete this strategy?",
    content: `Deleting ... `,
    onOk: () => {
      deleteUser(row.original.id)
        .then(() => {
          toast.success("Strategy deleted successfully!");
        })
        .catch((error: any) => {
          console.error("❌ Delete failed:", error);

          let message = "Failed to delete strategy. Please try again.";

          // ✅ Extract API error message if available
          if (error.response?.data) {
            const apiError = error.response.data;

            if (typeof apiError.error === "string") {
              message = apiError.error;
            }
          }

          toast.error(message);
        });
    },
  });
};


  const handleCopyRow = (row: any) => {
    if (!row) return;
    const rowData = row.original; // Get the row data
    let textToCopy = JSON.stringify(rowData, null, 2); // Convert to formatted JSON
     if (navigator?.clipboard?.writeText) {
    navigator.clipboard.writeText(textToCopy).catch((err) => {
      console.error("Clipboard write failed:", err);
    });
  } else {
    const textarea = document.createElement("textarea");
    textarea.value = textToCopy;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }// Copy to clipboard

    let jsonData;

    try {
      jsonData = JSON.parse(textToCopy);
      setStrategyName(jsonData.name);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Copied Successfully");
      setInputBackgroundColour("#83bf6e26");
      setTimeout(() => {
        setInputBackgroundColour(""); // Then set it again
      }, 2400);
    } catch (error) {
      console.error("Invalid JSON. Pasted as a string:", textToCopy);
      jsonData = { rawText: textToCopy }; // Fallback if not valid JSON
    }
  };

  interface ContextMenuPosition {
    mouseX: number;
    mouseY: number;
  }

  const [contextMenu, setContextMenu] = useState<ContextMenuPosition | null>(
    null
  );

  // Function to handle right-click and open the menu
  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 }
        : null
    );
  };

  // Function to handle menu close
  const handleClose = () => {
    setContextMenu(null);
  };

  // Function to handle Paste action
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      let jsonData;

      try {
        jsonData = JSON.parse(text);
        setStrategyName(jsonData.name);
        window.scrollTo({ top: 0, behavior: "smooth" });
        toast.success("Copied and Pasted Successfully");
        setInputBackgroundColour("#83bf6e26");
        setTimeout(() => {
          setInputBackgroundColour(""); // Then set it again
        }, 2400);
      } catch (error) {
        console.error("Invalid JSON. Pasted as a string:", text);
        jsonData = { rawText: text }; // Fallback if not valid JSON
      }
    } catch (err) {
      console.error("Failed to read clipboard contents:", err);
    }

    handleClose();
  };

  // Function to handle Mark action
  const handleResizing = () => {
    setShowRowResizing(!showRowResizing);
    handleClose();
  };

  const handleExportRows = async (rows: MRT_Row<any>[]) => {
    if (rows.length === 0) {
      toast.warn("Please select at least one row to export");
      return;
    }

    try {
      const rowIds = rows.map((row) => row.original.id);
      const response = await axiosInstance.post(
        `${backendbaseUrl}/paper-app/strategies/select-export/`,
        { ids: rowIds },
        {
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "selected_strategies.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("Export started successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export selected rows");
    }
  };

  const handleDataDownload = async () => {
    try {
      const response = await axiosInstance.get(
        `${backendbaseUrl}/paper-app/strategies/export-delete`,
        {
          responseType: "blob",
          headers: {
            // Add any required headers
            "Content-Type": "application/json",
          },
        }
      );

      // Check if response is valid
      if (!response.data) {
        throw new Error("No data received from server");
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "all_strategies.csv");
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success("All data download started");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download all data");
    }
  };

  const handleBulkDelete = async (rows: MRT_Row<any>[]) => {
    // 1. Get selected row IDs
    const rowIds = rows.map((row) => row.original.id);

    if (rowIds.length === 0) {
      toast.warn("Please select at least one strategy to delete.");
      return;
    }

    // 2. Debug: Verify selected IDs before sending
    console.log("Selected IDs for Deletion:", rowIds);

    try {
      // 3. Show confirmation dialog
      showDeleteConfirmModal({
        title: `Delete ${rowIds.length} Selected Strategies?`,
        content: `This will Permanently Delete ${rowIds.length} Strategies. Continue?`,
        onOk: async () => {
          const loadingToast = toast.loading(
            `Deleting ${rowIds.length} Strategy(s)...`
          );
          try {
            // 4. Prepare exact payload format API expects
            const payload = {
              ids: rowIds.filter((id) => id), // Ensure no empty/null IDs
            };

            // 5. Debug: Verify final payload
            console.log("Sending delete payload:", payload);

            // 6. Make API call
            const response = await axiosInstance.delete(
              `${backendbaseUrl}/paper-app/strategies/export-delete/`,
              {
                data: payload,
                headers: {
                  "Content-Type": "application/json",
                  "X-Requested-With": "XMLHttpRequest",
                },
              }
            );

            // 7. Handle response
            if (response.status === 200) {
              toast.success(
                `Successfully deleted ${rowIds.length} strategy(s)`
              );

              // 8. Refresh data and clear selection
              queryClient.invalidateQueries({ queryKey: ["users"] });
              table.setRowSelection({});
            } else {
              throw new Error(
                response.data?.message || "Unknown error occurred"
              );
            }
          } catch (error: any) {
            // 9. Enhanced error handling
            console.error("Delete error details:", {
              message: error.message,
              response: error.response?.data,
              status: error.response?.status,
            });

            const errorMessage =
              error.response?.data?.detail ||
              error.response?.data?.message ||
              error.message ||
              "Failed to Delete Strategies";

            toast.error(`Deletion Failed: ${errorMessage}`);
          } finally {
            toast.dismiss(loadingToast);
          }
        },
      });
    } catch (error) {
      console.error("Confirmation dialog error:", error);
      toast.error("An error occurred during the deletion process.");
    }
  };

  const table = useMaterialReactTable({
    columns,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableClickToCopy: true,
    enableKeyboardShortcuts: true,
    enableColumnActions: true,
    enableColumnDragging: true,
    enableGlobalFilter: false,
    enableGrouping: true,
    enableHiding: true,
    enableSorting: true,
    enableColumnPinning: true,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onDensityChange: setDensity,
    onGlobalFilterChange: setGlobalFilter,
    onShowColumnFiltersChange: setShowColumnFilters,
    onShowGlobalFilterChange: setShowGlobalFilter,
    onIsFullScreenChange: setIsFullScreen,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onColumnSizingChange: setColumnSizing,
    onSortingChange: setSorting,
    onGroupingChange: setGrouping,
    manualSorting: true,
    manualPagination: true,
    enableRowSelection: true,
    // positionToolbarAlertBanner: "bottom",
    enableColumnResizing: true,
    // columnResizeMode: "onEnd",
    // defaultColumn: {
    //   minSize: 50,
    //   enableResizing: true,
    // },
    enableFilterMatchHighlighting: true,
    rowCount: totalCount,
    manualFiltering: true,
    data: data?.results || [],
    enableDensityToggle: false,
    createDisplayMode: "row",
    editDisplayMode: "row",
    enableEditing: true,

    muiTopToolbarProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
        "& .MuiBox-root.css-wsew38": {
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        },
      },
      className: "top-toolbar-wrapper__new",
    }),

    muiTableContainerProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
      },
      className: "table-container-wrapper__new",
    }),
    muiTableFooterProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
      },
      className: "table-footer-wrapper__new",
    }),
    muiBottomToolbarProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
      },
      className: "bottom-toolbar-wrapper__new",
    }),

    muiTableBodyRowProps: ({ row, table }) => ({
      onKeyDown: (event: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (event.key === "Enter") {
          event.preventDefault();

          const editingRow = table.getState().editingRow;
          if (editingRow) {
            const saveHandler = table.options.onEditingRowSave;
            if (saveHandler) {
              const newedit = {
                ...editingRow.original,
                ...editingRow._valuesCache,
              };
              console.log("Saving:", newedit);

              saveHandler({
                exitEditingMode: () => table.setEditingRow(null),
                row: editingRow,
                table,
                values: newedit,
              });
            }
          }
        }
      },
    }),

    enableColumnOrdering: true,
    enableRowOrdering: false,
    getRowId: (row) => row.id,
    enableColumnFilterModes: true,
    // initialState: { showColumnFilters: true },
    filterFns: {
      customFilterFn: (row, id, filterValue) => {
        return row.getValue(id) === filterValue;
      },
    },

    // defaultDisplayColumn: {
    //   enableResizing: true,
    // },
    displayColumnDefOptions: {
      "mrt-row-actions": {
        enableColumnActions: true,
        size: 120,
        muiTableHeadCellProps: {
          // align: "center",
        },
      },
      "mrt-row-numbers": {
        enableColumnDragging: true,
        enableColumnOrdering: true,
        // enableResizing: true,
        muiTableHeadCellProps: {
          sx: {
            fontSize: "1.2rem",
          },
        },
      },
      "mrt-row-select": {
        enableColumnActions: true,
        enableHiding: true,
        // size: 50,
      },
    },

    renderTopToolbarCustomActions: ({ table }) => {
      const hasSelection = table.getIsSomeRowsSelected() || table.getIsAllRowsSelected();

      const buttonsMap: Record<string, React.ReactNode> = {
        "upload": (
          <Button startIcon={<UploadFileIcon />} onClick={handleFileUploadOpen}>
            Upload File
          </Button>
        ),
        "export_all": (
          <Button
            onClick={() => handleDataDownload()}
            startIcon={<BrowserUpdatedIcon />}
          >
            Export All Data
          </Button>
        ),
        "history": (
          <Button
          //export all data that is currently in the table (ignore pagination, sorting, filtering, etc.)
            onClick={() => handlehistory()}
            startIcon={<HistoryIcon />}
          >
            History
          </Button>
        ),
        "template": (
          <BrokerTemplateDownloadButton
            apiEndpoint="/paper-app/download-strategy-template/"
            buttonText="Download Template"
            fileName="strategy_template.csv"
            sx={{}}
          />
        ),
        "export_selected": (
          <Button
            disabled={!hasSelection}
            onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
            startIcon={<FileDownloadIcon />}
          >
            Export Selected Rows
          </Button>
        ),
        "delete_selected": hasSelection ? (
          <Button
            style={{ color: "red" }}
            onClick={() => handleBulkDelete(table.getSelectedRowModel().rows)}
            startIcon={<DeleteIcon />}
          >
            Delete Selected ({table.getSelectedRowModel().rows.length})
          </Button>
        ) : null,
        "reset": (
          <ResetTablePreferencesButton onOk={() => resetTableState()} />
        ),
      };

      return (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <Box
            className="table-download__wrapper"
            sx={{
              display: "flex",
              gap: "6px",
              padding: "0px",
              flexWrap: "wrap",
            }}
          >
            <SortableContext
              items={currentActionButtonOrder}
              strategy={horizontalListSortingStrategy}
            >
              {currentActionButtonOrder.map((id) => (
                buttonsMap[id] && actionButtonVisibility[id] !== false ? (
                  <SortableActionButton key={id} id={id} tooltipText={buttonsConfig[id].tooltipText}>
                    {buttonsMap[id]}
                  </SortableActionButton>
                ) : null
              ))}
            </SortableContext>
          </Box>
        </DndContext>
      );
    },

    renderToolbarInternalActions: ({ table }) => {
      const hasSelection = table.getIsSomeRowsSelected() || table.getIsAllRowsSelected();
      const inactiveIds = hasSelection ? [] : ["delete_selected"];

      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TableActionVisibilityMenu
            actionButtonOrder={currentActionButtonOrder}
            setActionButtonOrder={setActionButtonOrder}
            actionButtonVisibility={actionButtonVisibility}
            setActionButtonVisibility={setActionButtonVisibility}
            buttonsConfig={buttonsConfig}
            defaultOrder={defaultButtonOrder}
            inactiveIds={inactiveIds}
          />
          {/* Render the default internal actions (like Show/Hide columns, Density, etc.) */}
          <MRT_ToggleFiltersButton table={table} />
          <MRT_ShowHideColumnsButton table={table} />
          <MRT_ToggleDensePaddingButton table={table} />
          <MRT_ToggleFullScreenButton table={table} />
        </Box>
      );
    },

    muiTableHeadCellProps: {
      sx: {
        border: "1px solid #e0e0e087",
        fontStyle: "normal",
        fontWeight: "normal",
      },
    },

    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "0",
        border: "0",
      },
    },
    localization: {
      filterCustomFilterFn: "Custom Filter Fn",
      toggleDensity: `Toggle Density (${density})`,
    } as any,

    muiToolbarAlertBannerProps: isLoadingUsersError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,

    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateUser,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleeditsave,

    renderRowActions: ({ row, table }) => (
      <Box>
        <Tooltip title="Edit" className="editIcon">
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete" className="deleteIcon">
          <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy Row">
          <IconButton onClick={() => handleCopyRow(row)}>
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),

    state: {
      columnFilters,
      columnVisibility,
      columnOrder,
      columnPinning,
      columnSizing,
      density,
      globalFilter,
      grouping,
      pagination,
      showColumnFilters,
      showGlobalFilter,
      isFullScreen,
      isLoading: isLoadingUsers,
      isSaving: isCreatingUser || isUpdatingUser || isDeletingUser,
      showAlertBanner: isLoadingUsersError,
      showProgressBars: isFetchingUsers,
    },
  });

  const transformedFilters = (columnFilters as MRT_ColumnFiltersState).map((filter) => ({
    ...filter,
    mode: (table.getColumn(filter.id)?.getFilterFn() as any)?.name || "contains",
  }));

  useEffect(() => {
    const transformedFilters = (columnFilters as MRT_ColumnFiltersState).map((filter) => ({
      ...filter,
      mode: (table.getColumn(filter.id)?.getFilterFn() as any)?.name || "contains",
    }));

    setFilterModes(transformedFilters);
  }, [columnFilters, table]);

  const someEventHandler = (event: any) => {
    console.info("rows :", table.getRowModel().rows);
    console.info("row model :", table.getSelectedRowModel());
    console.info("row model :", table.getState().sorting);
  };

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header className="accordion_button">
                    Add New Strategies
                  </Accordion.Header>
                  <Accordion.Body>
                    <Form onSubmit={formCreate}>
                      <div className="form_contain">
                        <div
                          className={`flex formWrapper ${
                            inputBackgroundColour !== "" ? "inputboxcustom" : ""
                          }`}
                          style={{ backgroundColor: inputBackgroundColour }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              p: { xs: 1, sm: 1 },
                              pt: "20px !important",
                              backgroundColor: "#ffff",
                              borderRadius: "4px",
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <Box
                              className="form_default"
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "flex-start",
                                justifyContent: "start",
                                gap: 0.5,
                                "& > *": {
                                  minWidth: {
                                    xs: "100%",
                                    sm: "48%",
                                    md: "23%",
                                    lg: "15%",
                                    xl: "7.5%",
                                  },
                                },
                              }}
                            >
                              <CompactTextField
                                label="Enter Strategies"
                                value={strategyName}
                                onChange={(value) => setStrategyName(value)}
                                name="strategiesName"
                                error={Boolean(
                                  formErrors.strategyName &&
                                    touched.strategyName
                                )}
                                helperText={touched.strategyName && formErrors.strategyName ? formErrors.strategyName : ""}
                                validateOnBlur={validateOnBlur}
                                slotProps={{
                                  inputLabel: {
                                    shrink: true,
                                  },
                                }}
                                onFocus={() => handleFocus("strategyName")}
                                sx={{
                                  maxWidth: { sm: "250px", xs: "100%" },
                                }}
                              />
                              <Button
                                variant="contained"
                                fullWidth
                                type="submit"
                                sx={{
                                  height: { xs: "36px", sm: "32px" },
                                  flex: { xs: "1 1 auto", sm: "0 0 auto" },
                                  minWidth: { xs: "100%", sm: "100px" },
                                  maxWidth: { sm: "150px" },
                                  backgroundColor: "#00bcd4",
                                  textTransform: "none",
                                  fontSize: "0.75rem",
                                  "&:hover": {
                                    backgroundColor: "#008c9e",
                                  },
                                }}
                              >
                                Add
                              </Button>
                            </Box>
                          </Box>
                        </div>
                      </div>
                    </Form>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
              <div
                className="table-ad__wrapper"
                onContextMenu={handleContextMenu}
              >
                <div className="table-ad__wrapperCard">
                  <MaterialReactTable table={table} />

                  {/* Context Menu */}
                  <Menu
                    open={contextMenu !== null}
                    onClose={handleClose}
                    anchorReference="anchorPosition"
                    anchorPosition={
                      contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                    }
                  >
                    <MenuItem onClick={handleResizing}>
                      {showRowResizing ? "Disable Resizing" : "enable Resizing"}
                    </MenuItem>
                  </Menu>
                </div>
                <FileUpload
                  open={openFileUpload}
                  onFileUploadClose={handleFileUploadClose}
                  onFileUploadSubmit={handleFileUploadSubmit}
                  acceptedFileTypes=".csv,.json"
                />
              </div>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};

//CREATE hook (post new user to api)
function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (user: User) => {
      //send api update request here
      await new Promise((resolve) => setTimeout(resolve, 1000)); //fake api call
      return Promise.resolve();
    },
    //client side optimistic update
    onMutate: (newUserInfo: User) => {
      queryClient.setQueryData(
        ["users"],
        (prevUsers: any) =>
          [
            ...prevUsers,
            {
              ...newUserInfo,
              id: (Math.random() + 1).toString(36).substring(7),
            },
          ] as User[]
      );
    },
  });
}

//READ hook (get users from api)
function useGetData(
  filterModes: { mode: string; id: string; value: unknown }[],
  pagination: { pageIndex: number; pageSize: number },
  setTotalCount: any,
  sorting: any
) {
  return useQuery<{ results: User[]; totalCount: number }>({
    queryKey: ["users", filterModes, pagination, sorting],
    queryFn: async () => {
      let url = `${backendbaseUrl}/paper-app/strategies/?page=${
        pagination.pageIndex + 1
      }&page_size=${pagination.pageSize}`;

      if (filterModes.length > 0) {
        console.log("filterModes---,", filterModes);
        const queryParams = filterModes
          .map(
            (e) =>
              `fieldName__d39d26f3-8442-44d0-8a7f-66cfcbb2c781__${
                e.id
              }=${String(e.value)}__d39d26f3-8442-44d0-8a7f-66cfcbb2c781__${
                e.mode
              }`
          )
          .join("&");

        url += `&${queryParams}`;
      }
      console.log("sortparams:", sorting);
      if (sorting.length > 0) {
        const sortParams = `sortField_${sorting[0].id}=${sorting[0].desc}_sorting`;
        url += `&${sortParams}`;

        console.log("yesss");
      } else {
        console.log("sortParams");
      }

      const response = await axiosInstance.get(url);
      console.log(response.data);
      setTotalCount(response.data.count);
      return {
        results: response.data.results as User[],
        totalCount: response.data.count as number,
      };
    },
    refetchOnWindowFocus: false,
  });
}

function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (user: User) => {
      console.group("🔄 User Update Mutation");
      console.log("Input User Object:", user);

      // ✅ Validation before API call
      if (!user || !user.id) {
        console.error("❌ Invalid user object:", user);
        throw new Error("User object is invalid or missing ID");
      }

      if (!user.strategy || user.strategy.trim() === "") {
        console.error("❌ Missing required field: strategy");
        throw new Error("Strategy field is required");
      }

      const payload = user;
      console.log("📦 Prepared Payload:", payload);
      console.groupEnd();

      try {
        const { data } = await axiosInstance.put(
          `/paper-app/strategies/${payload.id}/`,
          user
        );
        console.log("✅ API Response:", data);
        return data;
      } catch (error: any) {
        console.error("❌ API Request Failed:", error);

        let message = "Failed to update user. Please try again.";

        if (error.response?.data) {
          const apiErrors = error.response.data;

          if (apiErrors.name && Array.isArray(apiErrors.name)) {
            message = apiErrors.name[0];
          } else if (typeof apiErrors.error === "string") {
            const match = apiErrors.error.match(/ErrorDetail\(string='(.+?)'/);
            if (match && match[1]) {
              message = match[1];
            } else {
              message = apiErrors.error;
            }
          }
        }

        throw new Error(message); // ❌ Don't toast here
      }
    },

    onMutate: async (newUserInfo: User) => {
      console.log("🔮 Optimistic Update - New User Info:", newUserInfo);

      await queryClient.cancelQueries({ queryKey: ["users"] });

      const previousUsers = queryClient.getQueryData(["users"]);

      queryClient.setQueryData(["users"], (oldData: any) =>
        oldData?.map((prevUser: User) =>
          prevUser.id === newUserInfo.id ? newUserInfo : prevUser
        )
      );

      return { previousUsers };
    },

    onError: (err: any, newUser, context) => {
      console.error("❌ Update Failed:", err);

      // ✅ Only ONE place shows toast
      if (err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error("Update failed. Please try again.");
      }

      queryClient.setQueryData(["users"], context?.previousUsers);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}



//DELETE hook (delete user in api)
function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      await axiosInstance.delete(
        `${backendbaseUrl}/paper-app/strategies/${userId}/`
      );
    },
    onMutate: async (userId: string) => {
      await queryClient.cancelQueries({ queryKey: ["users"] });

      const previousUsers = queryClient.getQueryData<User[]>(["users"]);

      queryClient.setQueryData(["users"], (oldUsers: User[] | undefined) =>
        oldUsers?.filter((user) => user.id !== userId)
      );

      return { previousUsers };
    },
    onError: (err, userId, context) => {
      console.error("Error deleting user:", err);
      if (context?.previousUsers) {
        queryClient.setQueryData(["users"], context.previousUsers);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

const ReactQueryDevtoolsProduction = lazy(() =>
  import("@tanstack/react-query-devtools/build/modern/production.js").then(
    (d) => ({
      default: d.ReactQueryDevtools,
    })
  )
);

const queryClient = new QueryClient();

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Example />
      {/* <Suspense fallback={null}>
        <ReactQueryDevtoolsProduction />
      </Suspense> */}
    </QueryClientProvider>
  );
}

const validateRequired = (value: string) => !!value.length;
const validateEmail = (email: string) =>
  !!email.length &&
  email
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );

function validateUser(user: User) {
  return {
    // strategyName: !validateRequired(user.name)
    //   ? "Strategy Name is Required"
    //   : "",
    // name: !validateRequired(user.name) ? "Last Name is Required" : "",
    // email: !validateEmail(user.email) ? "Incorrect Email Format" : "",
  };
}
